import * as React from "react";
import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { ClaimStateFilterEnum, GraphQLTypes } from "kw/api/zeus";
import { displayRegistry } from "kw/util/DisplayRegistry";
import { getQuestionSetAnswers } from "kw/util/getQuestionSetAnswers";
import { isNullish } from "kanopi-components/utils/primitives";
import { LabelSmall } from "kanopi-components/typography";
import { Placeholder } from "kanopi-components/Placeholder";
import { StringDisplay } from "kw/util/DisplayRegistry/StringDisplay";
import { UnansweredDisplay } from "kw/util/DisplayRegistry/UnansweredDisplay";
import { useClaimState } from "../../hooks/useClaimState";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TClaimState = {
  claimProfileId: bigint;
  disabled: boolean;
  buttonText: string;
  update: TOnFormEventFunc;
  filterBy?: GraphQLTypes["ClaimStateFilterEnum"];
};

type TStatelessClaimState = {
  questionSets: TQuestionSet[];
  data: TClaimData;
  update: TOnFormEventFunc;
  disabled: boolean;
  buttonText: string;
  filterBy: GraphQLTypes["ClaimStateFilterEnum"];
};

export const ClaimState: React.FC<TClaimState> = ({
  claimProfileId,
  filterBy = ClaimStateFilterEnum.SUMMARY,
  ...restProps
}) => {
  const { claimState, loading } = useClaimState({ claimProfileId, filterBy });
  return (
    <Placeholder loading={loading}>
      <StatelessClaimState
        questionSets={claimState?.questionSets}
        data={claimState?.claimProfile?.data}
        filterBy={filterBy}
        {...restProps}
      />
    </Placeholder>
  );
};

export const StatelessClaimState = (props: TStatelessClaimState) => {
  const {
    questionSets = [],
    data = {},
    disabled,
    update,
    buttonText,
    filterBy,
    ...restProps
  } = props;
  return (
    <Block display="flex" flexDirection="column" alignItems="center" {...restProps}>
      <table data-testid={`claimState-${filterBy}`}>
        <tbody>
          {questionSets.map((qs, ii) => (
            <ClaimStateQuestionSet
              key={qs.id}
              questionSet={qs}
              data={data}
              withPadding={ii < questionSets.length - 1}
            />
          ))}
        </tbody>
      </table>
      {!disabled && (
        <Block marginTop="scale1200" marginBottom="scale800">
          {!!update && (
            <Button
              kind={Button.KIND.primary}
              size={Button.SIZE.mini}
              shape={Button.SHAPE.pill}
              onClick={update}
            >
              {buttonText ?? "Update"}
            </Button>
          )}
        </Block>
      )}
    </Block>
  );
};

type TClaimStateQuestionSetProps = {
  questionSet: TQuestionSet;
  data: TClaimData;
  withPadding?: number;
};

const ClaimStateQuestionSet: React.FC<TClaimStateQuestionSetProps> = (props) => {
  const { questionSet, data, withPadding } = props;
  const answers = getQuestionSetAnswers(questionSet, data);
  const questions = questionSet?.questions || [];
  return (
    <>
      {questions.map((q, ii) => (
        <ClaimStateQuestion
          key={q.id}
          question={q}
          answer={answers[q.id]}
          withPadding={withPadding || ii < questions.length - 1}
        />
      ))}
    </>
  );
};

type TClaimStateQuestionProps = {
  question: TQuestion;
  answer: GraphQLTypes["AnswerType"];
  withPadding?: number;
};

const ClaimStateQuestion: React.FC<TClaimStateQuestionProps> = (props) => {
  const { question, answer, withPadding } = props;
  const DisplayComponent = isNullish(answer)
    ? UnansweredDisplay
    : displayRegistry.getByType(question?.inputType, StringDisplay);
  const [css, theme] = useStyletron();
  return (
    <tr style={{ verticalAlign: "top" }}>
      <td
        className={css({
          width: "50%",
          paddingBottom: withPadding ? theme.sizing.scale600 : null,
          paddingRight: theme.sizing.scale800,
          textAlign: "right",
        })}
      >
        <LabelSmall color={theme.colors.contentTertiary}>{question?.label}</LabelSmall>
      </td>
      <td
        className={css({
          width: "50%",
          paddingBottom: withPadding ? theme.sizing.scale600 : null,
          fontSize: theme.typography.LabelSmall.fontSize,
        })}
      >
        {/* TODO: Make type/interface for Display Components? */}
        <DisplayComponent question={question} answer={answer} />
      </td>
    </tr>
  );
};
